import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  createUserWithEmailAndPassword,
  getAdditionalUserInfo,
  getAuth,
  GoogleAuthProvider,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type User,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const firebaseEnabled = Boolean(
  firebaseConfig.apiKey && firebaseConfig.authDomain && firebaseConfig.projectId && firebaseConfig.appId,
);

let app: FirebaseApp | null = null;

export function firebaseApp(): FirebaseApp {
  if (!firebaseEnabled) {
    throw new Error("Firebase web config is missing.");
  }
  app = app || initializeApp(firebaseConfig);
  return app;
}

function auth() {
  const app = firebaseApp();
  return getAuth(app);
}

export function listenToAuth(callback: (user: User | null) => void): () => void {
  if (!firebaseEnabled) {
    callback(null);
    return () => {};
  }
  // Firebase emits this observer for sign-in/sign-out and whenever an ID token
  // is refreshed. Keeping the UI session snapshot subscribed prevents child
  // transports that receive a token string from holding an expired bearer.
  return onIdTokenChanged(auth(), callback);
}

export async function currentToken(user: User | null, forceRefresh = false): Promise<string | null> {
  return user ? user.getIdToken(forceRefresh) : null;
}

export interface AuthIdentityResult {
  uid: string;
  isNewUser: boolean;
}

export async function loginWithGoogle(): Promise<AuthIdentityResult> {
  const credential = await signInWithPopup(auth(), new GoogleAuthProvider());
  return {
    uid: credential.user.uid,
    isNewUser: getAdditionalUserInfo(credential)?.isNewUser === true,
  };
}

export async function loginWithEmail(email: string, password: string): Promise<AuthIdentityResult> {
  const credential = await signInWithEmailAndPassword(auth(), email.trim(), password);
  return { uid: credential.user.uid, isNewUser: false };
}

export interface EmailSignUpResult extends AuthIdentityResult {
  accountCreated: true;
  profileUpdated: boolean;
}

export async function signUpWithEmail(
  email: string,
  password: string,
  displayName?: string,
): Promise<EmailSignUpResult> {
  const credential = await createUserWithEmailAndPassword(auth(), email.trim(), password);
  const normalizedName = displayName?.trim();
  if (normalizedName) {
    try {
      await updateProfile(credential.user, { displayName: normalizedName });
    } catch {
      // Account creation is the irreversible/auth-critical step. A transient
      // profile write must not be reported as a failed sign-up after Firebase
      // has already created and signed in the account.
      return {
        uid: credential.user.uid,
        isNewUser: true,
        accountCreated: true,
        profileUpdated: false,
      };
    }
  }
  return {
    uid: credential.user.uid,
    isNewUser: true,
    accountCreated: true,
    profileUpdated: true,
  };
}

export async function logout(): Promise<void> {
  if (firebaseEnabled) {
    await signOut(auth());
  }
}
